import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { REPORT_LIST_PATH } from '@constants/routes';
import { REPORT_IMAGE_SRC } from '@constants/images';
import { useTranslation } from 'react-i18next';
import {
    getReportDeviceData,
    fetchDeviceChangeLogData,
    fetchDeviceChangeLogDataByPatientName,
    getIndividualDeviceData,
    getDataUsingDeviceUuid,
    getNurseReportDeviceData,
    fetchAllPatientsForHospital,
} from '@utils/hospitalUtils';
import { getDataUsingDeviceId, userDetail } from '@utils/sharedUtils';
import {
    HOSPITAL_DEVICE_TABLE,
    NURSE_DEVICE_TABLE,
    DEVICE_TABLE,
} from '@constants/dynamoDBTableName';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import Select, { SingleValue } from 'react-select';
import { useDispatch } from 'react-redux';
import { setReportData, setReportParams } from '@store/reducers/userSlice';
import { ADMIN, HOSPITAL, INDIVIDUAL, NURSE } from '@constants/constants';

interface Device {
    device_id: string;
    device_name: string;
    hospital_id: string;
    uuid: string;
    patient_name?: string;
}

interface DeviceOption {
    value: string;
    label: string;
}

interface PatientOption {
    value: string;
    label: string;
}

const ReportSubmitPage = () => {
    const [inputValue, setInputValue] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);
    const loggedInHospitalUuId = useAppSelector(
        (state) => state.login.loggedInHospitalUuId
    );
    const dispatch = useDispatch();
    const [deviceData, setDeviceData] = useState<Device[]>([]);
    const [selectedDeviceOption, setSelectedDeviceOption] =
        useState<SingleValue<DeviceOption>>(null);
    const [selectedPatientOption, setSelectedPatientOption] =
        useState<SingleValue<PatientOption>>(null);
    const [patientOptions, setPatientOptions] = useState<any[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [changeLogData, setChangeLogData] = useState<any[]>([]);
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if ((!selectedDeviceOption && !selectedPatientOption) || !startDate || !endDate) {
            setError('Please select a device ID or patient name, start date, and end date');
            return;
        }

        try {
            let allLogData: any[] = [];

            if (selectedDeviceOption) {
                // If device is selected, we can always use fetchDeviceChangeLogData
                // It now supports optional patientName filtering
                allLogData = await fetchDeviceChangeLogData(
                    selectedDeviceOption.value,
                    startDate,
                    endDate,
                    selectedPatientOption?.value
                );
            } else if (selectedPatientOption) {
                // If only patient is selected, use the patient name fetcher
                allLogData = await fetchDeviceChangeLogDataByPatientName(
                    selectedPatientOption.value,
                    startDate,
                    endDate
                );
            }

            const uniqueLogData = allLogData;

            // Assuming logData is an array, map through the entries
            const reportData = await Promise.all(
                uniqueLogData.map(async (log) => {
                    // Fetch nurse device data
                    const DeviceData = await getDataUsingDeviceId(
                        DEVICE_TABLE,
                        log.device_id?.S ? log.device_id?.S : ''
                    );
                    // Access the uuid from the first item in the Items array
                    const deviceUuid = DeviceData?.Items?.[0]?.uuid?.S || '';
                    const nurseDeviceDatas =
                        await getDataUsingDeviceUuid(deviceUuid);
                    const nurseDevice = nurseDeviceDatas?.Items?.[0];
                    let nurseName = '';
                    let nurseId = '';
                    let nurseUuid = '';

                    if (nurseDevice) {
                        nurseId = nurseDevice.user_id?.S || '';
                        nurseUuid = nurseDevice.uuid?.S || '';
                        // Fetch nurse user details
                        const nurseUserData = await userDetail(nurseId);
                        nurseName = nurseUserData?.name?.S || '';
                    }

                    const getVal = (attr: any) => (attr && typeof attr === 'object' && 'S' in attr ? attr.S : attr);
                    const getNum = (attr: any) => (attr && typeof attr === 'object' && 'N' in attr ? attr.N : attr);

                    const rawCreatedAt = getVal(log.created_at);
                    let formattedDate = '';
                    let formattedTime = '';

                    if (rawCreatedAt) {
                        const dateObj = new Date(rawCreatedAt);
                        // Format the date as DD/MM/YYYY
                        formattedDate = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;

                        // Format the time as HH:MM AM/PM
                        let hours = dateObj.getHours();
                        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        hours = hours % 12 || 12;
                        formattedTime = `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
                    }

                    return {
                        id: getVal(log.uuid) || getVal(log.id) || '',
                        device_id: getVal(log.device_id) || '',
                        patient_name: getVal(log.patient_name) || '',
                        medicine_name: getVal(log.medicine_name) || '',
                        nurse_name: nurseName,
                        nurse_id: nurseId,
                        nurse_uuid: nurseUuid,
                        status: getVal(log.status) || '',
                        created_at: formattedDate,
                        time: formattedTime,
                        rpm: getNum(log.RPM) || '0',
                        re_saline: getNum(log.Re_saline) || '0',
                        t_qua: getNum(log.T_Qua) || '0',
                        createdAt_time: getVal(log.created_at) || '',
                    };
                })
            );

            // Dispatch formatted report data to global state
            dispatch(setReportData(reportData));
            dispatch(setReportParams({
                device_id: selectedDeviceOption?.value,
                patient_name: selectedPatientOption?.value,
                start_date: startDate,
                end_date: endDate
            }));
            navigate(REPORT_LIST_PATH);
        } catch (err) {
            setError('Error fetching device change log data');
            console.error(err);
        }
    };

    const fetchDeviceData = async () => {
        try {
            let devices: any[] = [];
            if (loggedInUserRole.toLowerCase() == HOSPITAL.toLowerCase()) {
                devices = await getReportDeviceData(
                    HOSPITAL_DEVICE_TABLE,
                    loggedInHospitalUuId
                );
            } else if (loggedInUserRole.toLowerCase() == NURSE.toLowerCase()) {
                devices = await getNurseReportDeviceData(
                    NURSE_DEVICE_TABLE,
                    loggedInUserID
                );
            } else if (loggedInUserRole.toLowerCase() == INDIVIDUAL.toLowerCase()) {
                devices = await getIndividualDeviceData(
                    loggedInUserTenantID
                );
            }
            setDeviceData(devices);

            // Fetch all patients for these specific devices to populate dropdown
            if (devices.length > 0) {
                const deviceIds = devices.map(d => d.device_id).filter(id => !!id);
                const patients: string[] = await fetchAllPatientsForHospital(deviceIds);
                setPatientOptions(patients.map((p: string) => ({ value: p, label: p })));
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchDeviceData();
    }, [loggedInUserRole, loggedInHospitalUuId, loggedInUserID]);

    const selectStyles = {
        control: (provided: any) => ({
            ...provided,
            height: '46px',
            borderRadius: '10px',
            border: '1.5px solid #E2E8F0',
            fontSize: '14px',
            fontWeight: 500,
            color: '#0F172A',
            boxShadow: 'none',
            width: '100%',
            '&:hover': {
                borderColor: '#CBD5E1',
            },
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: '#94A3B8',
        }),
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '10px 0' }}>
            <div
                style={{
                    background: '#fff',
                    borderRadius: '24px',
                    padding: '40px',
                    width: '100%',
                    maxWidth: '1024px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                    display: 'flex',
                    gap: '40px',
                    flexWrap: 'wrap',
                }}
            >
                {/* ── Left Column: Description, Stage Illustration, Banner ── */}
                <div style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                        <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#0F172A', margin: '0 0 6px 0', letterSpacing: '-0.3px' }}>
                            Create a New Report
                        </h2>
                        <p style={{ fontSize: '13.5px', color: '#64748B', margin: 0, lineHeight: 1.5, fontWeight: 500 }}>
                            Select device, patient and date range to generate your report.
                        </p>

                        {/* High fidelity Stage Pedestal Illustration */}
                        <div
                            style={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '36px auto',
                                width: '280px',
                                height: '280px',
                            }}
                        >
                            {/* Soft radial blue background glow */}
                            <div
                                style={{
                                    position: 'absolute',
                                    width: '220px',
                                    height: '220px',
                                    borderRadius: '50%',
                                    background: 'radial-gradient(circle, rgba(59,130,246,0.09) 0%, rgba(59,130,246,0) 70%)',
                                    top: '5%',
                                    left: '10%',
                                    zIndex: 1,
                                }}
                            />

                            {/* Dripset device image */}
                            <img
                                src={REPORT_IMAGE_SRC}
                                alt="Create Report Device"
                                style={{
                                    height: '210px',
                                    objectFit: 'contain',
                                    position: 'relative',
                                    zIndex: 2,
                                }}
                            />

                            {/* 3D Pedestal ellipse bottom stage */}
                            <div
                                style={{
                                    width: '230px',
                                    height: '26px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(180deg, #EFF6FF 0%, #DBEAFE 100%)',
                                    border: '1.5px solid #BFDBFE',
                                    position: 'absolute',
                                    bottom: '10px',
                                    zIndex: 1,
                                    boxShadow: '0 6px 12px rgba(191,219,254,0.3)',
                                }}
                            />

                            {/* Sparkles background */}
                            <div style={{ position: 'absolute', top: '20%', left: '8%', color: '#93C5FD', fontSize: '18px', fontWeight: 'bold', zIndex: 1 }}>✦</div>
                            <div style={{ position: 'absolute', bottom: '30%', right: '5%', color: '#93C5FD', fontSize: '18px', fontWeight: 'bold', zIndex: 1 }}>✦</div>
                        </div>
                    </div>

                    {/* Bottom verification badge */}
                    <div
                        style={{
                            background: '#EFF6FF',
                            border: '1.5px solid #DBEAFE',
                            borderRadius: '12px',
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <div
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: '#DBEAFE',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                        </div>
                        <span style={{ fontSize: '12px', color: '#1E40AF', fontWeight: 600, lineHeight: 1.4 }}>
                            Accurate data. Better insights. Improved patient outcomes.
                        </span>
                    </div>
                </div>

                {/* ── Right Column: Form Fields + Submit ── */}
                <div style={{ flex: '1.2 1 450px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {error && (
                            <div
                                style={{
                                    background: '#FEF2F2',
                                    border: '1px solid #FCA5A5',
                                    color: '#B91C1C',
                                    borderRadius: '8px',
                                    padding: '10px 14px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                            >
                                {error}
                            </div>
                        )}

                        {/* Select Device ID Row */}
                        <div>
                            <label htmlFor="device_id" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                                {t('report.select_device_id')}
                            </label>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div
                                    style={{
                                        width: '46px',
                                        height: '46px',
                                        borderRadius: '10px',
                                        background: '#EFF6FF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                                        <rect x="5" y="2" width="14" height="20" rx="2" />
                                        <line x1="5" y1="8" x2="19" y2="8" />
                                        <rect x="8" y="11" width="8" height="6" rx="1" fill="#DBEAFE" />
                                        <circle cx="12" cy="5" r="1.5" fill="#2563EB" />
                                    </svg>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Select
                                        id="device_id"
                                        name="device_id"
                                        options={deviceData.map((device) => ({
                                            value: device.device_id,
                                            label: `${device.device_name} (${device.device_id})`,
                                        }))}
                                        placeholder="Select The Device ID"
                                        value={selectedDeviceOption}
                                        styles={selectStyles}
                                        onChange={(selectedOption) => setSelectedDeviceOption(selectedOption)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Patient Name Row */}
                        <div>
                            <label htmlFor="patient_name" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                                {t('report.Paitent_Name')}
                            </label>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div
                                    style={{
                                        width: '46px',
                                        height: '46px',
                                        borderRadius: '10px',
                                        background: '#EFF6FF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                        <circle cx="12" cy="7" r="4" />
                                    </svg>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Select
                                        id="patient_name"
                                        name="patient_name"
                                        options={patientOptions}
                                        placeholder="Select The Patient Name"
                                        value={selectedPatientOption}
                                        styles={selectStyles}
                                        onChange={(selectedOption) => setSelectedPatientOption(selectedOption)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Date Range Fields */}
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {/* Start Date */}
                            <div style={{ flex: 1, minWidth: '200px' }}>
                                <label htmlFor="startDate" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                                    {t('report.select_start_date')}
                                </label>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div
                                        style={{
                                            width: '46px',
                                            height: '46px',
                                            borderRadius: '10px',
                                            background: '#EFF6FF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </div>
                                    <input
                                        type="date"
                                        id="startDate"
                                        style={{
                                            flex: 1,
                                            height: '46px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #E2E8F0',
                                            padding: '10px 14px',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#0F172A',
                                            outline: 'none',
                                        }}
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* End Date */}
                            <div style={{ flex: 1, minWidth: '200px' }}>
                                <label htmlFor="endDate" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                                    {t('report.select_end_date')}
                                </label>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div
                                        style={{
                                            width: '46px',
                                            height: '46px',
                                            borderRadius: '10px',
                                            background: '#EFF6FF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </div>
                                    <input
                                        type="date"
                                        id="endDate"
                                        style={{
                                            flex: 1,
                                            height: '46px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #E2E8F0',
                                            padding: '10px 14px',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#0F172A',
                                            outline: 'none',
                                        }}
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            style={{
                                background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                height: '50px',
                                fontSize: '15px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                marginTop: '12px',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 6px 18px rgba(37,99,235,0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 14px rgba(37,99,235,0.3)';
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                            </svg>
                            Generate Report
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReportSubmitPage;
